import Lottie from "lottie-react";
import { useEffect, useRef, useState } from "react";
import { createPopper } from "@popperjs/core";
import useIsMobile from "../../hooks/useIsMobile";
import useIsTab from "../../hooks/useIsTab";

export const OutlinedButton = ({
  bgColor,
  onClick,
  Icon,
  isFocused,
  tooltip,
  badge,
  lottieOption,
  disabledOpacity,
  renderRightComponent,
  disabled,
  large,
  btnID,
  color,
  focusIconColor,
  isRequestProcessing,
  borderColor,
  buttonText,
  className,
  hasSelector = false
}) => {
  const iconSize = 24 * (large ? 1.7 : 1);

  const [mouseOver, setMouseOver] = useState(false);
  const [mouseDown, setMouseDown] = useState(false);
  const [blinkingState, setBlinkingState] = useState(1);
  const [tooltipShow, setTooltipShow] = useState(false);

  const isMobile = useIsMobile();
  const isTab = useIsTab();

  const btnRef = useRef(null);
  const tooltipRef = useRef(null);
  const popperInstanceRef = useRef(null);
  const intervalRef = useRef(null);

  const openTooltip = () => {
    if (!btnRef.current || !tooltipRef.current) return;
    if (!popperInstanceRef.current) {
      popperInstanceRef.current = createPopper(btnRef.current, tooltipRef.current, {
        placement: "top",
        modifiers: [{ name: "offset", options: { offset: [-55, 5] }}]
      });
    } else {
      popperInstanceRef.current.update();
    }
    setTooltipShow(true);
  };

  const closeTooltip = () => setTooltipShow(false);

  const startBlinking = () => {
    intervalRef.current = setInterval(() => {
      setBlinkingState((s) => (s === 1 ? 0.4 : 1));
    }, 600);
  };

  const stopBlinking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setBlinkingState(1);
  };

  useEffect(() => {
    if (isRequestProcessing) startBlinking();
    else stopBlinking();
    return () => stopBlinking();
  }, [isRequestProcessing]);

  useEffect(() => {
    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy();
        popperInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <div className="inline-flex items-stretch" role="group">
        <button
          ref={btnRef}
          id={btnID}
          type="button"
          disabled={disabled}
          onClick={onClick}
          onMouseEnter={() => {
            if (!disabled) {
              setMouseOver(true);
              openTooltip();     
            }
          }}
          onMouseLeave={() => {
            if (!disabled) {
              setMouseOver(false);
              closeTooltip();
            }
          }}
          onMouseDown={() => {
            if (!disabled) setMouseDown(true);
          }}
          onMouseUp={() => {
            if (!disabled) setMouseDown(false);
          }}
          className={`${disabled ? "cursor-default" : "cursor-pointer"}
            flex items-center justify-center ${className || ""}
            ${hasSelector ? "rounded-l-lg" : "rounded-lg"}
            ${bgColor ? `${bgColor}` : isFocused ? "bg-white" : "bg-gray-750"} 
              ${mouseOver
                ? "border-2 border-transparent border-solid"
                : borderColor
                  ? `border-2 border-[${borderColor}] border-solid`
                  : bgColor
                    ? "border-2 border-transparent border-solid"
                    : "border-2 border-solid border-[#ffffff33]"
              } ${!hasSelector ? "md:m-2 m-1" : ""}`}
          style={{
            transition: "all 200ms",
            transitionTimingFunction: "ease-in-out",
            opacity: blinkingState
          }}
        >
          <div
            className={`flex items-center justify-center p-1 m-1`}
            style={{
              opacity: disabled ? disabledOpacity || 0.7 : 1,
              transform: `scale(${mouseOver ? (mouseDown ? 0.95 : 1.1) : 1})`,
              transition: `all ${200 * 1}ms`,
              transitionTimingFunction: "linear",
            }}
          >
            {Icon &&
              (lottieOption ? (
                <div className={`flex items-center justify-center`}>
                  <div
                    style={{
                      height: iconSize,
                      width:
                        (iconSize * lottieOption?.width) /
                        lottieOption?.height,
                    }}
                  >
                    <Lottie
                      loop={lottieOption.loop}
                      autoPlay={lottieOption.autoPlay}
                      animationData={lottieOption.animationData}
                      rendererSettings={{
                        preserveAspectRatio:
                          lottieOption.rendererSettings.preserveAspectRatio,
                      }}
                      isClickToPauseDisabled
                    />
                  </div>
                </div>
              ) : (
                <>
                  <Icon
                    style={{
                      color: isFocused
                        ? focusIconColor || "#1C1F2E"
                        : color
                          ? color
                          : "#fff",
                      height: iconSize,
                      width: iconSize,
                    }}
                    fillcolor={
                      isFocused
                        ? focusIconColor || "#1C1F2E"
                        : color
                          ? color
                          : "#fff"
                    }
                  />
                  {badge && (
                    <p
                      className={`${isFocused ? "text-black" : "text-white"
                        } text-base ml-2`}
                    >
                      {badge}
                    </p>
                  )}
                </>
              ))}
          </div>
          {buttonText ? (
            <p className={`text-sm ${isFocused ? "text-gray-750" : "text-white"} font-semibold mr-2 text-center`}>
              {buttonText}
            </p>
          ) : null} 
        </button>
        {typeof renderRightComponent === "function" && renderRightComponent()}
      </div>
      {(!isMobile && !isTab) && (
        <div
          style={{ zIndex: 999 }}
          className={`${tooltipShow && (mouseOver || mouseDown) ? "" : "hidden"}
            pointer-events-none overflow-hidden flex flex-col items-center justify-center whitespace-pre-line`}
          ref={tooltipRef}
        >
          <div className="rounded-md p-1.5 bg-black">
            <p className="text-xs text-white">{tooltip || ""}</p>
          </div>
        </div>
      )}
    </>
  );
};
